import styled from 'styled-components';
import type { KsPackage, KsPackageIssue } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import {
  packageStatusLabel,
  packageStatusTone,
} from '@shared/lib/smetaLabels';

export const STEPS = [
  { key: 'setup', label: 'Договор и период' },
  { key: 'build', label: 'Сборка' },
  { key: 'lines', label: 'Строки' },
  { key: 'materials', label: 'Материалы' },
  { key: 'issues', label: 'Находки' },
  { key: 'forms', label: 'Формы и выгрузка' },
] as const;

export type StepKey = (typeof STEPS)[number]['key'];

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const ActionsRow = styled(Row)`
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FrozenMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const ErrorMessage = styled(Err)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Banner = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
`;

const KfoBanner = styled(Banner)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const KfoMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const StepBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const PaddedStepBar = styled(StepBar)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const StepButton = styled.button<{ $active?: boolean; $done?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active, $done }) =>
      $active ? theme.colors.primary : $done ? theme.colors.status.success : theme.colors.border};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  color: inherit;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

function packageLabel(p: KsPackage): string {
  const contract = p.contract_no ? `дог. № ${p.contract_no}` : (p.contract_label ?? 'договор');
  return `КС-3 № ${p.ks3_no} · ${contract}`;
}

export interface KsPackageHeaderProps {
  pkg: KsPackage;
  actsCount: number;
  allLinesCount: number;
  step: StepKey;
  onSelectStep: (step: StepKey) => void;
  stepDone: (step: StepKey) => boolean;
  stepBadge: (step: StepKey) => { text: string; tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' } | null;
  canWrite: boolean;
  frozen: boolean;
  busy: boolean;
  building: boolean;
  issuing: boolean;
  sendingRequest: boolean;
  isAuthor: boolean;
  hasOverContract: boolean;
  hasIssues: boolean;
  kfoGateIssue?: KsPackageIssue | null;
  onBuild: (id: string) => void;
  onIssue: (id: string) => void;
  onSendDsRequest: () => void;
  onDelete: (pkg: KsPackage) => void;
}

export function KsPackageHeader({
  pkg,
  actsCount,
  allLinesCount,
  step,
  onSelectStep,
  stepDone,
  stepBadge,
  canWrite,
  frozen,
  busy,
  building,
  issuing,
  sendingRequest,
  isAuthor,
  hasOverContract,
  hasIssues,
  kfoGateIssue,
  onBuild,
  onIssue,
  onSendDsRequest,
  onDelete,
}: KsPackageHeaderProps) {
  return (
    <>
      <Row>
        <div>
          <Title>{packageLabel(pkg)}</Title>
          <Muted>
            {pkg.period_from} — {pkg.period_to} · актов: {actsCount} · строк:{' '}
            {allLinesCount} · пересборка № {pkg.rebuild_no}
            {pkg.estimate_missing ? ' · ЭТАЛОН НЕ НАЙДЕН' : ''}
          </Muted>
        </div>
        <ActionsRow>
          <Badge $tone={packageStatusTone(pkg.status)}>
            {packageStatusLabel(pkg.status)}
          </Badge>
          <Button
            $variant="primary"
            $size="sm"
            type="button"
            onClick={() => onBuild(pkg.id)}
            disabled={building || !canWrite || frozen || busy}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {busy ? 'Собирается…' : 'Собрать'}
          </Button>
          {pkg.status === 'built' && (
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              onClick={() => onIssue(pkg.id)}
              disabled={issuing || !canWrite}
              title="Зафиксировать выпуск пакета КС и передать выполнение в БДР Экономиста"
            >
              {issuing ? 'Выпускаю…' : 'Выпустить в БДР'}
            </Button>
          )}
          {(hasOverContract || hasIssues) && (
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              onClick={onSendDsRequest}
              disabled={sendingRequest || !canWrite}
              title="Отправить заявку на подготовку дополнительного соглашения в Статут / Экономиста"
            >
              {sendingRequest ? 'Отправляю…' : 'Заявка на ДС'}
            </Button>
          )}
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            onClick={() => onDelete(pkg)}
            disabled={!canWrite || frozen || !isAuthor}
            title={
              !canWrite
                ? 'Только чтение'
                : !isAuthor
                  ? 'Удалить пакет может только его автор'
                  : undefined
            }
          >
            Удалить
          </Button>
        </ActionsRow>
      </Row>

      {pkg.status === 'build_failed' && pkg.error_message && (
        <ErrorMessage>{pkg.error_message}</ErrorMessage>
      )}
      {frozen && (
        <FrozenMuted>
          Пакет выпущен — состав заморожен: пересборка и правки запрещены.
        </FrozenMuted>
      )}
      {kfoGateIssue && (
        <KfoBanner>
          <strong>Контроль ПТО / КФО:</strong> {kfoGateIssue.message}
          <KfoMuted>
            Это мягкое предупреждение не блокирует выпуск форм, но снять его можно только
            после подписания АОСР в ПТО или принятия приёмочной заявки (RFI) в КФО.
          </KfoMuted>
        </KfoBanner>
      )}

      <PaddedStepBar>
        {STEPS.map((s, idx) => {
          const badge = stepBadge(s.key);
          return (
            <StepButton
              key={s.key}
              type="button"
              $active={step === s.key}
              $done={stepDone(s.key)}
              onClick={() => onSelectStep(s.key)}
            >
              <span>
                {idx + 1}. {s.label}
              </span>
              {stepDone(s.key) && <span aria-label="шаг пройден">✓</span>}
              {badge && <Badge $tone={badge.tone}>{badge.text}</Badge>}
            </StepButton>
          );
        })}
      </PaddedStepBar>
    </>
  );
}
